import type { Content, ContentRelationshipField, SupportTypes } from '@cobliteam/prismic';
import { dashboardClient, supportClient } from '@cobliteam/prismic';
import { type QueryKey, type UseQueryOptions, useQuery } from '@tanstack/react-query';

export const QUERY_KEY = ['PRISMIC'] as QueryKey;
export const QUERY_KEY_DASHBOARD_BANNER = [...QUERY_KEY, 'DASHBOARD', 'BANNER'] as QueryKey;
export const QUERY_KEY_LOGIN_BANNER = [...QUERY_KEY, 'LOGIN'] as QueryKey;
export const QUERY_KEY_NEWS_SIDEBAR = [...QUERY_KEY, 'NEWS', 'SIDEBAR'] as QueryKey;
export const QUERY_KEY_NEWS_PAGE = [...QUERY_KEY, 'NEWS', 'PAGE'] as QueryKey;
export const QUERY_KEY_ANNOUNCEMENT = [...QUERY_KEY, 'ANNOUNCEMENT'] as QueryKey;
export const QUERY_KEY_SUPPORT_PAGE = [...QUERY_KEY, 'SUPPORT', 'PAGE'] as QueryKey;

function isHomeBannerItemDocument(
  item: ContentRelationshipField<'home_banner_item'> | undefined
): item is ContentRelationshipField<'home_banner_item'> & Content.HomeBannerItemDocument {
  return !!item && 'data' in item && typeof item.data === 'object';
}

const useDashboardBanner = ({ options }: { options?: Omit<UseQueryOptions<Content.HomeBannerItemDocument[]>, 'queryKey'> } = {}) => {
  return useQuery<Content.HomeBannerItemDocument[]>({
    queryKey: QUERY_KEY_DASHBOARD_BANNER,
    queryFn: () =>
      dashboardClient
        .getSingle('home_banner', {
          fetchLinks: [
            'home_banner_item.badge_text',
            'home_banner_item.badge_color',
            'home_banner_item.title',
            'home_banner_item.description',
            'home_banner_item.button_text',
            'home_banner_item.button_link',
            'home_banner_item.image',
            'home_banner_item.hascameraflag',
            'home_banner_item.analytics_track',
          ],
        })
        .then((res) =>
          res.data.banner_items
            .map((item) => {
              if (item?.item && isHomeBannerItemDocument(item.item)) {
                return item.item as Content.HomeBannerItemDocument;
              }
              return null;
            })
            .filter((item): item is Content.HomeBannerItemDocument => item !== null)
        ),
    ...options,
  });
};

const useLoginBanner = ({ options }: { options?: Omit<UseQueryOptions<Content.LoginPageDocument>, 'queryKey'> } = {}) => {
  return useQuery<Content.LoginPageDocument>({
    queryKey: QUERY_KEY_LOGIN_BANNER,
    queryFn: () => dashboardClient.getSingle('login_page'),
    ...options,
  });
};

const useNewsSidebar = ({ options }: { options?: Omit<UseQueryOptions<Content.NewsSidebarDocument>, 'queryKey'> } = {}) => {
  const { data, isLoading, error } = useQuery<Content.NewsSidebarDocument>({
    queryKey: QUERY_KEY_NEWS_SIDEBAR,
    queryFn: () =>
      dashboardClient.getSingle('news_sidebar', {
        fetchLinks: ['news.image', 'news.uid', 'news.title', 'news.expired_at', 'news.resume', 'news.link', 'news.description', 'news.type'],
      }),
    ...options,
  });

  if (!data) return { data: [], content: null, isLoading, error };

  const newsItems = data?.data.sidebar
    ?.map(({ news }) => {
      const newsData = news as ContentRelationshipField<'news_sidebar'> & Content.NewsDocument;
      if (!newsData) return undefined;
      return newsData;
    })
    .filter((item) => item !== undefined);

  return {
    data: newsItems,
    content: data,
    isLoading,
    error,
  };
};

const useNewsPage = ({ options }: { options?: Omit<UseQueryOptions<Content.NewsDocument[]>, 'queryKey'> } = {}) => {
  return useQuery<Content.NewsDocument[]>({
    queryKey: QUERY_KEY_NEWS_PAGE,
    queryFn: () =>
      dashboardClient.getAllByType('news', {
        orderings: [{ field: 'document.first_publication_date', direction: 'desc' }],
      }),
    ...options,
  });
};

const useAnnouncement = ({ options }: { options?: Omit<UseQueryOptions<Content.AnnouncementCardDocument[]>, 'queryKey'> } = {}) => {
  return useQuery<Content.AnnouncementCardDocument[]>({
    queryKey: QUERY_KEY_ANNOUNCEMENT,
    queryFn: () =>
      dashboardClient.getAllByType('announcement_card', {
        orderings: [{ field: 'last_publication_date', direction: 'desc' }],
      }),
    ...options,
  });
};

const useGetSupportPostByUID = ({ dashboardRouterId, options }: { dashboardRouterId: string; options?: Omit<UseQueryOptions<SupportTypes.BlogPostDocument>, 'queryKey'> }) => {
  return useQuery<SupportTypes.BlogPostDocument>({
    queryKey: [QUERY_KEY_SUPPORT_PAGE, dashboardRouterId],
    queryFn: async () => {
      const response = await supportClient.getByUID('blog_post', dashboardRouterId, {
        fetchLinks: ['blog_post.relations', 'blog_post.title', 'blog_post.resume', 'blog_post.slices', 'blog_post.image'],
      });
      return response;
    },
    ...options,
  });
};

export type { Content, ContentRelationshipField };

export { useAnnouncement, useDashboardBanner, useLoginBanner, useNewsSidebar, useNewsPage, useGetSupportPostByUID };
